import { isTimePermissionEnabled } from '../config/applicationFeatures';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { AppCard } from './AppCard';
import { ProfileAvatar } from './ProfileAvatar';
import type { ColorPalette } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { WeeklyAttendanceEmployeeReport } from '../types/weeklyAttendanceReport';
import {
  formatWeekOfLabel,
  formatWeeklyDateLabel,
  formatWeeklyHours,
  formatWeeklyPermissionStatus,
  formatWeeklyStatusLabel,
  weeklyCheckInDisplay,
  weeklyCheckOutDisplay,
  weeklyHighlightCheckOut,
} from '../utils/weeklyAttendanceReport';
import { resolveWorkHoursCoverage } from '../utils/workHoursCoverage';
import {
  actualTotalWorkMinutes,
  formatTotalWorkHours,
  resolveAttendanceSpreadMinutes,
  resolveTotalHoursCoverage,
} from '../utils/totalHours';

type Props = {
  report: WeeklyAttendanceEmployeeReport;
  onPress?: () => void;
};

function statusColor(
  tone: WeeklyAttendanceEmployeeReport['days'][number]['statusTone'] | undefined,
  statusCode: string,
  palette: ColorPalette,
): string {
  switch (tone) {
    case 'present':
      return palette.success;
    case 'absent':
      return palette.error;
    case 'late':
      return palette.error;
    case 'leave':
      return palette.warning;
    case 'wfh':
      return palette.info;
    case 'shortHours':
      return palette.warning;
    default:
      break;
  }
  switch (statusCode) {
    case 'ON_TIME':
    case 'PRESENT':
      return palette.success;
    case 'ABSENT':
    case 'HALF_DAY':
      return palette.error;
    case 'LATE':
    case 'SHORT_HOURS':
    case 'MISSING_CHECKOUT':
      return palette.warning;
    default:
      return palette.textSecondary;
  }
}

function dayTotalWorkMinutes(
  day: WeeklyAttendanceEmployeeReport['days'][number],
): number | null {
  return actualTotalWorkMinutes({
    workedMinutes: day.workedMinutes,
    breakMinutes: day.lunchBreakMinutes,
    grossWorkMinutes: day.totalMinutes,
  });
}

function daySpreadMinutes(
  day: WeeklyAttendanceEmployeeReport['days'][number],
): number | null {
  return resolveAttendanceSpreadMinutes({
    spreadMinutes: day.spreadMinutes,
    expectedWorkMinutes: day.expectedWorkMinutes,
    policyLunchBreakMinutes: day.policyLunchBreakMinutes,
    actualBreakMinutes: day.lunchBreakMinutes,
  });
}

function weeklyTotalHoursLabel(
  day: WeeklyAttendanceEmployeeReport['days'][number],
): string {
  if (day.totalHours) return day.totalHours;
  return formatTotalWorkHours(dayTotalWorkMinutes(day));
}

/** Total Work Hours vs Spread Duration: green when covered, red when short. */
function totalHoursColor(
  day: WeeklyAttendanceEmployeeReport['days'][number],
  palette: ColorPalette,
): string {
  const label = weeklyTotalHoursLabel(day);
  if (label === '—' || label === '') return palette.textSecondary;
  const coverage = resolveTotalHoursCoverage(
    dayTotalWorkMinutes(day),
    daySpreadMinutes(day),
  );
  if (coverage === 'not_covered') return palette.error;
  if (coverage === 'covered') return palette.success;
  return palette.textSecondary;
}

/** Worked Hours vs Expected Work Hours: green when met, red when short. */
function workedHoursColor(
  day: WeeklyAttendanceEmployeeReport['days'][number],
  palette: ColorPalette,
): string {
  const label = day.workHours ?? formatWeeklyHours(day.workedMinutes);
  if (label === '—' || label === '') return palette.textSecondary;
  const coverage = resolveWorkHoursCoverage(
    day.workedMinutes,
    day.expectedWorkMinutes,
  );
  if (coverage === 'not_covered') return palette.error;
  if (coverage === 'covered') return palette.success;
  return palette.textSecondary;
}

export function WeeklyAttendanceReportCard({ report, onPress }: Props) {
  const palette = useAppColors();
  const weekLabel = formatWeekOfLabel(report.weekStart, report.weekEnd);
  const summary = report.summary;
  const HeaderWrapper = onPress ? Pressable : View;

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      email: { ...typography.caption, color: palette.textSecondary },
      week: { ...typography.caption, color: palette.textMuted, fontWeight: '600' as const },
      label: { ...typography.caption, color: palette.textMuted, fontWeight: '600' as const },
      value: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      headerCell: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '700' as const,
      },
    }),
    [palette],
  );

  return (
    <View>
      <AppCard style={styles.card}>
        <HeaderWrapper
          accessibilityRole={onPress ? 'button' : undefined}
          accessibilityLabel={
            onPress
              ? `${report.employeeName} weekly attendance report`
              : undefined
          }
          onPress={onPress}
        >
          <View style={styles.header}>
            <ProfileAvatar name={report.employeeName} size={44} />
            <View style={styles.headerText}>
              <Text style={themed.name}>{report.employeeName}</Text>
              <Text style={themed.email}>{report.employeeEmail || '—'}</Text>
              <Text style={themed.week}>{weekLabel}</Text>
            </View>
          </View>
        </HeaderWrapper>

        <Text style={[styles.section, { color: palette.textPrimary }]}>
          Weekly Summary
        </Text>
        <View style={styles.summaryGrid}>
          <SummaryItem label="Working Days" value={String(summary.totalWorkingDays)} palette={palette} />
          <SummaryItem label="Present" value={String(summary.presentDays)} palette={palette} />
          <SummaryItem label="LOP" value={String(summary.absentDays)} palette={palette} />
          <SummaryItem label="Late Days" value={String(summary.lateDays)} palette={palette} />
          <SummaryItem label="Short Hours Days" value={String(summary.shortHoursDays)} palette={palette} />
          <SummaryItem
            label="Total Worked"
            value={formatWeeklyHours(summary.totalWorkedMinutes)}
            palette={palette}
          />
          <SummaryItem
            label="Expected"
            value={formatWeeklyHours(summary.totalExpectedMinutes)}
            palette={palette}
          />
          <SummaryItem
            label="Total Late"
            value={formatWeeklyHours(summary.totalLateMinutes)}
            palette={palette}
          />
          <SummaryItem
            label="Total Short Hours"
            value={formatWeeklyHours(summary.totalShortHoursMinutes)}
            palette={palette}
          />
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          contentContainerStyle={styles.tableContent}
        >
          <View>
            <View style={[styles.tableHeader, { borderBottomColor: palette.border }]}>
              <Text style={[themed.headerCell, styles.colDate]}>Date</Text>
              <Text style={[themed.headerCell, styles.colTime]}>Check In</Text>
              <Text style={[themed.headerCell, styles.colTime]}>Check Out</Text>
              <Text style={[themed.headerCell, styles.colDuration]}>Break</Text>
              <Text style={[themed.headerCell, styles.colWorked]}>Work Hours</Text>
              <Text style={[themed.headerCell, styles.colWorked]}>
                Total Work Hours
              </Text>
              <Text style={[themed.headerCell, styles.colStatus]}>Status</Text>
              <Text style={[themed.headerCell, styles.colPermission]}>
                {isTimePermissionEnabled() ? 'Leave / Permission' : 'Leave'}
              </Text>
            </View>
            {report.days.map((day, index) => (
              <View
                key={day.date}
                style={[
                  styles.tableRow,
                  {
                    borderBottomColor: palette.border,
                    borderBottomWidth: index === report.days.length - 1 ? 0 : StyleSheet.hairlineWidth,
                  },
                ]}
                accessibilityLabel={`${formatWeeklyDateLabel(day.date)}, ${formatWeeklyStatusLabel(day)}${
                  formatWeeklyPermissionStatus(day) !== '—'
                    ? `, ${formatWeeklyPermissionStatus(day).replace(/\n/g, ', ')}`
                    : ''
                }`}
              >
                <Text style={[themed.value, styles.colDate, styles.tableValue]}>
                  {formatWeeklyDateLabel(day.date)}
                </Text>
                <Text style={[themed.value, styles.colTime, styles.tableValue]}>
                  {weeklyCheckInDisplay(day)}
                </Text>
                <Text
                  style={[
                    themed.value,
                    styles.colTime,
                    styles.tableValue,
                    weeklyHighlightCheckOut(day)
                      ? { color: palette.error, fontWeight: '700' }
                      : null,
                  ]}
                >
                  {weeklyCheckOutDisplay(day)}
                </Text>
                <Text style={[themed.value, styles.colDuration, styles.tableValue]}>
                  {day.breakTime ?? formatWeeklyHours(day.lunchBreakMinutes)}
                </Text>
                <Text
                  style={[
                    themed.value,
                    styles.colWorked,
                    styles.tableValue,
                    { color: workedHoursColor(day, palette) },
                  ]}
                >
                  {day.workHours ?? formatWeeklyHours(day.workedMinutes)}
                </Text>
                <Text
                  style={[
                    themed.value,
                    styles.colWorked,
                    styles.tableValue,
                    { color: totalHoursColor(day, palette) },
                  ]}
                >
                  {weeklyTotalHoursLabel(day)}
                </Text>
                <Text
                  style={[
                    styles.colStatus,
                    typography.caption,
                    {
                      color: statusColor(day.statusTone, day.statusCode, palette),
                      fontWeight: '700',
                    },
                  ]}
                >
                  {formatWeeklyStatusLabel(day)}
                </Text>
                <Text
                  style={[
                    styles.colPermission,
                    typography.caption,
                    { color: palette.textSecondary, fontWeight: '600' },
                  ]}
                >
                  {formatWeeklyPermissionStatus(day)}
                </Text>
              </View>
            ))}
          </View>
        </ScrollView>
      </AppCard>
    </View>
  );
}

function SummaryItem({
  label,
  value,
  palette,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.summaryItem}>
      <Text style={[typography.caption, { color: palette.textMuted }]}>{label}</Text>
      <Text style={[typography.body, { color: palette.textPrimary, fontWeight: '700' }]}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { gap: spacing.md, marginBottom: spacing.md },
  header: { flexDirection: 'row', gap: spacing.md, alignItems: 'center' },
  headerText: { flex: 1, gap: 2 },
  section: { ...typography.sectionTitle, fontSize: 16 },
  summaryGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  summaryItem: { width: '30%', minWidth: 96, gap: 2 },
  tableHeader: {
    flexDirection: 'row',
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: spacing.xs,
  },
  tableContent: { minWidth: 980 },
  tableValue: { fontSize: 13 },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: spacing.sm,
    gap: spacing.xs,
    alignItems: 'flex-start',
  },
  colDate: { width: 80 },
  colTime: { width: 180 },
  colDuration: { width: 80 },
  colWorked: { width: 90 },
  colStatus: { width: 170 },
  colPermission: { width: 220 },
});
